// src/Utils/scheduleUtils.ts

export interface TimeSlot {
  id: string;
  start: Date;
  end: Date;
  status: 'AVAILABLE' | 'BUSY' | 'NONE';
  title?: string;
}

export interface DayColumnData {
  date: Date;
  isoDate: string; // 'YYYY-MM-DD'
  dayName: string; // 'Mon', 'Tue'
  dayNumber: string; // '21'
  monthName: string; // 'Sep'
  isToday: boolean;
  isPast: boolean;
  isDisabled: boolean;
  slots: TimeSlot[];
}

export interface OfficeInterval {
  start: number; // Minutes from midnight, e.g., 540 = 09:00 AM
  end: number;   // Minutes from midnight, e.g., 1020 = 05:00 PM
}

export interface ScheduleData {
  manage_agenda?: boolean;
  slot_duration?: number; // Duration in minutes (e.g., 30)
  office_hours?: Record<string, OfficeInterval[]>; // Keys '0'-'6' for Sun-Sat
  busy_slots?: Array<{ start: string | Date; end: string | Date }>;
}

/**
 * Checks if a date is beyond maxMonths (default: 3 months) from today.
 */
export const isBeyondMaxDate = (date: Date, maxMonths = 3): boolean => {
  const limit = new Date();
  limit.setMonth(limit.getMonth() + maxMonths);
  limit.setHours(23, 59, 59, 999);
  return date > limit;
};

/**
 * Formats a Date object to an ISO date string (YYYY-MM-DD) in local time.
 */
export const toISODateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Generates slots for a specific target date based on office hours and busy slots.
 */
export const getSlotsForDate = (
  targetDate: Date,
  schedule: ScheduleData
): TimeSlot[] => {
  if (!schedule || !schedule.office_hours) return [];

  const dayKey = targetDate.getDay().toString(); // '0' = Sun, '1' = Mon, etc.
  const intervals = schedule.office_hours[dayKey] || [];
  if (intervals.length === 0) return [];

  const slotDuration = schedule.slot_duration || 30; // Default to 30 mins
  const slots: TimeSlot[] = [];

  // Parse busy slots for comparison
  const busyRanges = (schedule.busy_slots || []).map((b) => ({
    start: new Date(b.start).getTime(),
    end: new Date(b.end).getTime(),
  }));

  intervals.forEach((interval) => {
    let currentMin = interval.start;

    while (currentMin + slotDuration <= interval.end) {
      const slotStart = new Date(targetDate);
      slotStart.setHours(Math.floor(currentMin / 60), currentMin % 60, 0, 0);

      const slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotStart.getMinutes() + slotDuration);

      const slotStartTime = slotStart.getTime();
      const slotEndTime = slotEnd.getTime();

      // Check if slot overlaps with any busy range
      const isBusy = busyRanges.some(
        (b) => slotStartTime < b.end && slotEndTime > b.start
      );

      slots.push({
        id: `${toISODateString(targetDate)}_${currentMin}`,
        start: slotStart,
        end: slotEnd,
        status: isBusy ? 'BUSY' : 'AVAILABLE',
      });

      currentMin += slotDuration;
    }
  });

  return slots;
};

/**
 * Generates visible days starting from a base date. Default updated to 7 days for full-week rendering.
 */
export const getVisibleDays = (
  startDate: Date,
  daysCount = 7,
  maxMonths = 3,
  locale = 'en-US'
): DayColumnData[] => {
  const days: DayColumnData[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  for (let i = 0; i < daysCount; i++) {
    const current = new Date(start);
    current.setDate(start.getDate() + i);

    const isToday = current.getTime() === today.getTime();
    const isPast = current < today;

    days.push({
      date: current,
      isoDate: toISODateString(current),
      dayName: current.toLocaleDateString(locale, { weekday: 'short' }),
      dayNumber: current.toLocaleDateString(locale, { day: '2-digit' }),
      monthName: current.toLocaleDateString(locale, { month: 'short' }),
      isToday,
      isPast,
      isDisabled: isBeyondMaxDate(current, maxMonths),
      slots: [],
    });
  }

  return days;
};

/**
 * Safely parses ISO dates with microsecond precision (.147000)
 */
export const parseISODate = (isoString: string): Date => {
  if (!isoString) return new Date();
  const sanitized = isoString.replace(/(\.\d{3})\d+/, '$1');
  const normalized =
    sanitized.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(sanitized)
      ? sanitized
      : `${sanitized}Z`;
  return new Date(normalized);
};

/**
 * Calculates minutes from midnight in a specific timezone
 */
export const getMinutesFromMidnightInTZ = (
  date: Date,
  timeZone: string = 'America/Mexico_City'
): number => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  let hours = 0;
  let minutes = 0;

  for (const part of parts) {
    if (part.type === 'hour') hours = parseInt(part.value, 10) % 24;
    if (part.type === 'minute') minutes = parseInt(part.value, 10);
  }

  return hours * 60 + minutes;
};

/**
 * Formats YYYY-MM-DD key for a date in a specific timezone
 */
export const getLocalDateKeyInTZ = (
  date: Date,
  timeZone: string = 'America/Mexico_City'
): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
};

/**
 * Converts minute offsets (e.g. 540) to formatted time string (e.g. "9:00 AM")
 */
export const formatMinutesToTime = (
  minutes: number,
  locale = 'en-US'
): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const date = new Date();
  date.setHours(hours, mins, 0, 0);
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};

/**
 * Formats interval bounds
 */
export const formatInterval = (
  start: number | string,
  end: number | string,
  locale = 'en-US'
): string => {
  if (typeof start === 'number' && typeof end === 'number') {
    return `${formatMinutesToTime(start, locale)} - ${formatMinutesToTime(end, locale)}`;
  }
  return `${start} - ${end}`;
};

export interface BackendAgendaEntry {
  start: string; // "2026-10-01T18:00:00" or "2026-10-01T18:00:00Z"
  end: string;
  status?: string;
}

/**
  * Checks if a slot (start/end) overlaps with any busy agenda entry.
  */
export function isSlotBusy(slotStart: Date, slotEnd: Date, busyEntries: BackendAgendaEntry[]): boolean {
  const slotStartMs = slotStart.getTime();
  const slotEndMs = slotEnd.getTime();

  return busyEntries.some((entry) => {
    const entryStartMs = new Date(entry.start).getTime();
    const entryEndMs = new Date(entry.end).getTime();

    // Standard interval overlap formula:
    // Slot starts before entry ends AND slot ends after entry starts
    return slotStartMs < entryEndMs && slotEndMs > entryStartMs;
  });
}

/**
 * Calculates the ISO week number and ISO year for a given Date.
 */
export function getISOWeekAndYear(date: Date): { year: number; weekNumber: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNumber = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), weekNumber };
}